'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { isNativePlatform } from '@/lib/utils/platform'
import { createClient } from '@/lib/supabase/client'
import { useOnResume } from '@/hooks/use-on-resume'
import {
  authenticateWithBiometric,
  getBiometricAvailability,
  getBiometricState,
  readSessionBackup,
  restoreSessionFromBackup,
  setBiometricState,
  syncBackupFromSession,
  type BiometricKind,
  type BiometricState,
} from '@/lib/native/session-backup'

/**
 * Native session re-entry (phase 2): back up the refresh token, rebuild the
 * session silently at launch when the WebView lost its cookies, and gate the
 * app behind Face ID / fingerprint when the person turned that on.
 *
 * Mounted once by `<NativeSessionBackup>` in the native shell. Pure logic lives
 * in `lib/native/session-backup.ts`; this hook only schedules it.
 */

/** Away this long and the biometric lock comes back (same idea as banking apps). */
const RELOCK_AFTER_MS = 5 * 60 * 1000
/** Flush the backup after navigation settles; getSession reads a cookie, it is cheap. */
const SYNC_DEBOUNCE_MS = 1_000
/** At most this many silent restores per launch: a loop guard if cookies do not stick. */
const MAX_RESTORE_ATTEMPTS = 2
const ATTEMPTS_KEY = 'alumnia:session-restore-attempts'
const UNLOCKED_KEY = 'alumnia:biometric-unlocked'

export type SessionGate = 'none' | 'restoring' | 'locked'

export interface NativeSessionBackupState {
  gate: SessionGate
  /** Offer "Entrar con Face ID la próxima vez"; null when nothing to offer. */
  offer: { kind: BiometricKind } | null
  /** Biometric prompt for the lock was refused or cancelled: show the retry/other-method UI. */
  unlockFailed: boolean
  unlock: () => Promise<void>
  /** Dismiss the lock on the login screen: sign in another way. */
  skip: () => void
  acceptOffer: (reason: string) => Promise<void>
  declineOffer: () => Promise<void>
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}
function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // private mode: the module flag below still holds for this document
  }
}

/** Module flag survives client navigations; sessionStorage survives our own reloads. */
let unlockedThisLaunch = false
function isUnlocked(): boolean {
  return unlockedThisLaunch || readSession(UNLOCKED_KEY) === '1'
}
function markUnlocked(): void {
  unlockedThisLaunch = true
  writeSession(UNLOCKED_KEY, '1')
}

/** Test seam. */
export function __resetUnlockedForTests(): void {
  unlockedThisLaunch = false
}

/** Routes where the unlock offer makes sense: the signed-in app, not auth or onboarding. */
function isOfferRoute(pathname: string | null): boolean {
  return !!pathname && pathname.startsWith('/in/')
}

export function isLoginPath(pathname: string | null): boolean {
  return pathname === '/login' || !!pathname?.startsWith('/login/')
}

export function useNativeSessionBackup(reasonForUnlock: string): NativeSessionBackupState {
  const pathname = usePathname()
  const [gate, setGate] = useState<SessionGate>('none')
  const [offer, setOffer] = useState<{ kind: BiometricKind } | null>(null)
  const [unlockFailed, setUnlockFailed] = useState(false)
  const userIdRef = useRef<string | null>(null)
  const bioStateRef = useRef<BiometricState | null>(null)
  const restoreStartedRef = useRef(false)
  const reasonRef = useRef(reasonForUnlock)
  useEffect(() => {
    reasonRef.current = reasonForUnlock
  })

  /** Back up the live session; lock or offer according to the stored choice. */
  const syncNow = useCallback(async () => {
    const userId = await syncBackupFromSession(createClient())
    userIdRef.current = userId
    if (!userId) return
    const state = await getBiometricState(userId)
    bioStateRef.current = state
    if (state === 'on') {
      setOffer(null)
      if (!isUnlocked()) setGate('locked')
      return
    }
    if (state === null) {
      const availability = await getBiometricAvailability()
      setOffer(availability.available ? { kind: availability.kind } : null)
    } else {
      setOffer(null)
    }
  }, [])

  // Keep the backup fresh: on mount, after navigation settles, on token events.
  useEffect(() => {
    if (!isNativePlatform()) return
    const timer = setTimeout(() => void syncNow(), SYNC_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [pathname, syncNow])

  useEffect(() => {
    if (!isNativePlatform()) return
    const { data } = createClient().auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') void syncNow()
    })
    return () => data.subscription.unsubscribe()
  }, [syncNow])

  useOnResume(
    () => {
      if (!isNativePlatform()) return
      if (bioStateRef.current === 'on') {
        setUnlockFailed(false)
        setGate('locked')
      }
      void syncNow()
    },
    { minAwayMs: RELOCK_AFTER_MS }
  )

  // Silent restore: only on the login screen, only when there is no session.
  useEffect(() => {
    if (!isNativePlatform() || !isLoginPath(pathname) || restoreStartedRef.current) return
    // A forced sign-out (suspended/removed) lands here with ?error=: do not resurrect it.
    if (new URLSearchParams(window.location.search).has('error')) return
    const attempts = Number(readSession(ATTEMPTS_KEY) ?? '0')
    if (attempts >= MAX_RESTORE_ATTEMPTS) return
    restoreStartedRef.current = true

    void (async () => {
      const supabase = createClient()
      const { data } = await supabase.auth.getSession()
      if (data.session) return // the proxy would have redirected; nothing to restore
      const backup = await readSessionBackup()
      if (!backup) return

      setGate('restoring')
      const state = await getBiometricState(backup.userId)
      if (state === 'on') {
        setGate('locked')
        const result = await authenticateWithBiometric(reasonRef.current)
        if (result === 'cancelled' || result === 'failed') {
          // Stay on the login screen with the lock UI's other-method button.
          setUnlockFailed(true)
          restoreStartedRef.current = false
          return
        }
        setGate('restoring')
      }
      writeSession(ATTEMPTS_KEY, String(attempts + 1))
      const outcome = await restoreSessionFromBackup(supabase, backup)
      if (outcome === 'restored') {
        markUnlocked()
        window.location.replace('/')
        return
      }
      setGate('none')
    })()
  }, [pathname])

  const unlock = useCallback(async () => {
    setUnlockFailed(false)
    const result = await authenticateWithBiometric(reasonRef.current)
    if (result === 'success' || result === 'unavailable') {
      // 'unavailable': biometrics were removed from the device; do not trap the person.
      markUnlocked()
      setGate('none')
      if (!isLoginPath(window.location.pathname)) return
      // Login screen: let the restore effect continue.
      restoreStartedRef.current = false
      const backup = await readSessionBackup()
      if (backup) {
        setGate('restoring')
        const outcome = await restoreSessionFromBackup(createClient(), backup)
        if (outcome === 'restored') {
          window.location.replace('/')
          return
        }
        setGate('none')
      }
      return
    }
    setUnlockFailed(true)
  }, [])

  const skip = useCallback(() => {
    setUnlockFailed(false)
    setGate('none')
  }, [])

  const acceptOffer = useCallback(async (reason: string) => {
    const userId = userIdRef.current
    if (!userId) return
    // Prove it works before turning it on: a lock the person cannot open is a trap.
    const result = await authenticateWithBiometric(reason)
    if (result === 'success') {
      await setBiometricState(userId, 'on')
      bioStateRef.current = 'on'
      markUnlocked()
      setOffer(null)
    } else if (result === 'unavailable') {
      setOffer(null)
    }
  }, [])

  const declineOffer = useCallback(async () => {
    const userId = userIdRef.current
    setOffer(null)
    if (userId) {
      await setBiometricState(userId, 'declined')
      bioStateRef.current = 'declined'
    }
  }, [])

  return {
    gate,
    offer: isOfferRoute(pathname) ? offer : null,
    unlockFailed,
    unlock,
    skip,
    acceptOffer,
    declineOffer,
  }
}
